import { useQuestCompletionsV2 } from "@/components/QuestsV2/useQuestCompletions";
import { CheckIcon } from "@/components/icons/CheckIcon";
import { LockIcon } from "@/components/icons/LockIcon";
import { PlayGameIcon } from "@/components/icons/PlayGameIcon";
import { cn } from "@/lib/utils";
import { Heart } from "lucide-react";
import { useEffect } from "react";

export enum QuestType {
  WISHLIST = "wishlist",
  DOWNLOAD_LOGIN_PLAY = "login-play-game",
  LOGIN_PLAY_GAME = "login-play-quest",
  BONUS_GEMS = "bonus-gems",
  FREE_SPIN = "free-spin",
}

export type QuestListItem = {
  name: string;
  ab?: string;
  type: QuestType;
  gems?: number;
  gameId: string;
  questId: string;
  shortName?: string;
  loading?: boolean;
  onClick?: () => void;
};

export type ActivationQuest = {
  completed: boolean;
} & QuestListItem;

export const getQuestText = (
  quest: ActivationQuest,
): { text: string; subText: string } => {
  switch (quest.type) {
    case QuestType.WISHLIST:
      return { text: `Follow ${quest.name}`, subText: "On Immutable Play" };
    case QuestType.DOWNLOAD_LOGIN_PLAY:
      return {
        text: `Download & Login - ${quest.name}`,
        subText: "Login with Immutable",
      };
    case QuestType.LOGIN_PLAY_GAME:
      return {
        text: `Login & Play - ${quest.name}`,
        subText: "Login with Immutable",
      };
    case QuestType.BONUS_GEMS:
      return { text: quest.name, subText: "" };
    case QuestType.FREE_SPIN:
      return { text: "", subText: quest.name };
    default:
      return { text: "", subText: "" };
  }
};

export const getQuestIcon = (
  type: QuestType,
  completed?: boolean,
  loading?: boolean,
) => {
  const SpinnerDiv = loading ? (
    <div className="absolute border-2 border-transparent rounded-full border-t-status-success size-7 animate-rotate-border" />
  ) : null;

  if (type === QuestType.BONUS_GEMS || type === QuestType.FREE_SPIN) {
    return (
      <div className="w-9 flex-shrink-0 flex items-center justify-center">
        {SpinnerDiv}
        <LockIcon
          className={cn("size-5")}
          fill={completed ? "#ABF790" : "#F7F7F7"}
        />
      </div>
    );
  }

  if (completed) {
    return (
      <div className="size-9 flex-shrink-0 flex items-center justify-center">
        <CheckIcon className="size-6" />
      </div>
    );
  }

  return (
    <div className="relative size-9 flex-shrink-0 rounded-sm border border-white/10 flex items-center justify-center bg-neutral-900/25">
      {SpinnerDiv}
      {(() => {
        switch (type) {
          case QuestType.WISHLIST:
            return (
              <Heart
                className="size-5 mt-px"
                stroke="#FAFAFA"
                strokeWidth={2}
              />
            );
          case QuestType.DOWNLOAD_LOGIN_PLAY:
          case QuestType.LOGIN_PLAY_GAME:
            return <PlayGameIcon className="size-5" />;
          default:
            return null;
        }
      })()}
    </div>
  );
};

export const useActivationQuests = (
  questList: QuestListItem[],
  onListComplete?: (() => Promise<void>) | void,
) => {
  const { completedQuests, mutate } = useQuestCompletionsV2();

  const questCompleted = (gameId: string, questId: string) => {
    return Boolean(completedQuests?.[`${gameId}-${questId}`]?.completion_count);
  };

  const activationQuests: ActivationQuest[] = questList
    .map((quest) => {
      return {
        ...quest,
        completed: questCompleted(quest.gameId, quest.questId),
      };
    })
    // completed items first, then not completed items, then bonus gems/free spin
    .sort((a, b) => {
      if (a.type === QuestType.BONUS_GEMS || a.type === QuestType.FREE_SPIN)
        return 1;
      if (b.type === QuestType.BONUS_GEMS || b.type === QuestType.FREE_SPIN)
        return -1;
      if (a.completed && !b.completed) return -1;
      if (!a.completed && b.completed) return 1;
      return 0;
    });

  const nonBonusQuests = activationQuests.filter(
    (quest) =>
      quest.type !== QuestType.BONUS_GEMS && quest.type !== QuestType.FREE_SPIN,
  );

  const completedQuestsCount = nonBonusQuests.filter(
    (quest) => quest.completed,
  ).length;

  const totalQuestsCount = nonBonusQuests.length;

  useEffect(() => {
    if (completedQuestsCount < totalQuestsCount || !onListComplete) return;

    const triggerListComplete = async () => {
      await onListComplete();
    };

    triggerListComplete();
  }, [completedQuestsCount, totalQuestsCount, onListComplete]);

  return { activationQuests, completedQuestsCount, totalQuestsCount, mutate };
};
